import { useState, useEffect, useRef, useCallback } from 'react';
import type { ScoringProgress } from '@/components/pickems/ScoringProgressModal';

interface UseScoringProgressOptions {
  pickemId: string | null;
  enabled: boolean;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

interface SSEMessage {
  type: 'connected' | 'progress';
  data?: ScoringProgress;
}

export function useScoringProgress({
  pickemId,
  enabled,
  onComplete,
  onError,
}: UseScoringProgressOptions) {
  const [progress, setProgress] = useState<ScoringProgress | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const MAX_RECONNECT_ATTEMPTS = 5;

  // Keep refs up to date
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!pickemId || !enabled) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const url = `${apiUrl}/api/admin/pickems/${pickemId}/scoring-stream`;

    console.log(`[SSE] Connecting to ${url}`);

    const eventSource = new EventSource(url, {
      withCredentials: true,
    });

    eventSource.onopen = () => {
      console.log('[SSE] Connection opened');
      setIsConnected(true);
      reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
    };

    eventSource.onmessage = event => {
      try {
        const message: SSEMessage = JSON.parse(event.data);

        if (message.type === 'connected') {
          console.log('[SSE] Connected confirmation received');
        } else if (message.type === 'progress' && message.data) {
          console.log('[SSE] Progress update:', message.data);
          setProgress(message.data);

          // Handle completion or failure
          if (message.data.status === 'completed') {
            console.log('[SSE] Scoring completed');
            if (onCompleteRef.current) {
              onCompleteRef.current();
            }
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
            setIsConnected(false);
          } else if (message.data.status === 'failed') {
            console.error('[SSE] Scoring failed:', message.data.error);
            if (onErrorRef.current && message.data.error) {
              onErrorRef.current(message.data.error);
            }
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
            setIsConnected(false);
          }
        }
      } catch (error) {
        console.error('[SSE] Error parsing message:', error);
      }
    };

    eventSource.onerror = error => {
      console.error('[SSE] Connection error:', error);
      setIsConnected(false);
      eventSource.close();

      // Attempt to reconnect with exponential backoff
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current += 1;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000);
        console.log(
          `[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else {
        console.error('[SSE] Max reconnect attempts reached');
        if (onErrorRef.current) {
          onErrorRef.current('Connection lost. Please refresh the page.');
        }
      }
    };

    eventSourceRef.current = eventSource;
  }, [pickemId, enabled]);

  // Connect when enabled
  useEffect(() => {
    if (enabled && pickemId) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, pickemId, connect, disconnect]);

  return {
    progress,
    isConnected,
    disconnect,
  };
}
